import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, PROTOCOL_INFO, type AdminDevice, type Group, type Org, type Sim, type StaffRole, type Unregistered } from "../../lib/admin";
import DeviceCommands from "./DeviceCommands";
import CommandPresets from "./CommandPresets";
import { ago } from "../../lib/format";
import { ICONS, iconSvg, normIcon } from "../../lib/icons";
import { Drawer, Field, Notice, Pill, Toolbar, clean, fmtDateTime } from "./ui";

type Prefill = { unique_id?: string; name?: string; model?: string | null };

export default function Devices({ orgs, groups, role }: { orgs: Org[]; groups: Group[]; role: StaffRole }) {
  const [devices, setDevices] = useState<AdminDevice[] | null>(null);
  const [sims, setSims] = useState<Sim[]>([]);
  const [waiting, setWaiting] = useState<Unregistered | null>(null);
  const [waitErr, setWaitErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [edit, setEdit] = useState<AdminDevice | { prefill: Prefill } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [showPresets, setShowPresets] = useState(false);
  const [deepLink, setDeepLink] = useState(() => new URLSearchParams(window.location.search).get("device"));

  const load = useCallback(async () => {
    const [{ data: d }, { data: s }] = await Promise.all([
      supabase.rpc("admin_devices"),   // V3.9.0: all device records for staff, no positions
      supabase.from("sims").select("*").order("msisdn"),
    ]);
    setDevices((d ?? []).map((x: any) => ({ ...x, device_state: Array.isArray(x.device_state) ? x.device_state[0] ?? null : x.device_state })));
    setSims((s ?? []) as Sim[]);
    adminApi<Unregistered>("GET", "traccar-unregistered").then((w) => { setWaiting(w); setWaitErr(null); }).catch((e) => setWaitErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);
  // /admin/devices?device=<id> (links from notifications) opens that device
  useEffect(() => {
    if (!deepLink || !devices) return;
    const d = devices.find((x) => x.id === deepLink);
    if (d) setEdit(d);
    setDeepLink(null);
  }, [deepLink, devices]);

  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name ?? "—";
  const simNo = (id: string | null) => sims.find((s) => s.id === id)?.msisdn ?? null;
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (devices ?? []).filter((d) => (showRetired || d.status !== "retired") && (!orgFilter || d.org_id === orgFilter) &&
      (!s || [d.name, d.unique_id, d.model, simNo(d.sim_id)].some((v) => v?.toLowerCase().includes(s))));
  }, [devices, q, orgFilter, showRetired, sims]);

  const waitingList = [
    ...(waiting?.traccar ?? []).map((t) => ({ key: `t${t.traccar_id}`, unique_id: t.unique_id, name: t.name, model: t.model,
      note: `In Traccar${t.last_update ? ` · last report ${ago(t.last_update)}` : " · never reported"}` })),
    ...(waiting?.unmatched ?? []).filter((u) => !waiting?.traccar.some((t) => t.unique_id === u.unique_id))
      .map((u) => ({ key: `u${u.unique_id}`, unique_id: u.unique_id, name: "", model: null,
        note: `Sending data · ${u.hits} reports · last ${ago(u.last_seen)}` })),
  ];

  return (
    <section>
      <Toolbar>
        <h1>Devices</h1>
        <input className="asearch" placeholder="Search name, IMEI, SIM…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="asearch" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
          <option value="">All companies</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <label className="acheck"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired</label>
        {role === "skyward_admin" && <button className="btn-ghost" onClick={() => setShowPresets(true)}>Command presets</button>}
        <button className="btn-primary" onClick={() => setEdit({ prefill: {} })}>+ Add device</button>
      </Toolbar>
      {msg && <Notice onClose={() => setMsg(null)}>{msg}</Notice>}
      {waitErr && <Notice kind="warn">Couldn't check the tracking server for new devices: {waitErr}</Notice>}
      {waitingList.length > 0 && (
        <div className="await">
          <h3>Waiting to be registered ({waitingList.length})</h3>
          <p className="sub">These trackers are on the tracking server but not assigned to a company yet, so nobody can see them.</p>
          {waitingList.map((w) => (
            <div key={w.key} className="await-row">
              <span className="mono">{w.unique_id}</span><b>{w.name}</b><span className="sub">{w.note}</span>
              <button className="btn-ghost small" onClick={() => setEdit({ prefill: { unique_id: w.unique_id, name: w.name, model: w.model } })}>Register</button>
            </div>
          ))}
        </div>
      )}
      <div className="atable-wrap">
        <table className="atable">
          <thead><tr><th>Name</th><th>Company</th><th>Device ID / IMEI</th><th>Model</th><th>SIM</th><th>Last report</th><th>Status</th></tr></thead>
          <tbody>
            {devices === null && <tr className="empty"><td colSpan={7}>Loading…</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} onClick={() => setEdit(d)}>
                <td><span className="ticon" dangerouslySetInnerHTML={{ __html: iconSvg(d.icon, 15) }} /><b>{d.name}</b>{(d as any).is_simulated && <span className="apill info" style={{ marginLeft: 6 }} title="Contrail simulator — edit it in Admin → Simulator">Demo</span>}</td>
                <td>{orgName(d.org_id)}{d.group_id && <div className="sub">{groups.find((g) => g.id === d.group_id)?.name}</div>}</td>
                <td className="mono">{d.unique_id}</td>
                <td>{d.model ?? "—"}<div className="sub">{d.protocol ?? ""}</div></td>
                <td className="mono">{simNo(d.sim_id) ?? "—"}</td>
                <td>{d.device_state?.seen_at ? ago(d.device_state.seen_at) : <span className="sub">Never</span>}</td>
                <td><Pill value={d.status} /></td>
              </tr>
            ))}
            {devices && !rows.length && <tr className="empty"><td colSpan={7}>No devices found.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <DeviceForm device={"prefill" in edit ? null : edit} prefill={"prefill" in edit ? edit.prefill : {}}
          orgs={orgs} groups={groups} sims={sims} devices={devices ?? []} role={role} onClose={() => setEdit(null)}
          onSaved={async (m) => { setMsg(m); await load(); }} />
      )}
      {showPresets && <CommandPresets onClose={() => setShowPresets(false)} />}
    </section>
  );
}

function DeviceForm({ device, prefill, orgs, groups, sims, devices, role, onClose, onSaved }: {
  device: AdminDevice | null; prefill: Prefill; orgs: Org[]; groups: Group[]; sims: Sim[]; devices: AdminDevice[]; role: StaffRole;
  onClose: () => void; onSaved: (msg: string) => Promise<void>;
}) {
  const [f, setF] = useState({
    org_id: device?.org_id ?? "", name: device?.name ?? prefill.name ?? "", unique_id: device?.unique_id ?? prefill.unique_id ?? "",
    model: device?.model ?? prefill.model ?? "", protocol: device?.protocol ?? "h02", sim_id: device?.sim_id ?? "",
    status: device?.status ?? "active", notes: device?.notes ?? "", icon: device?.icon ? normIcon(device.icon) : "car", group_id: device?.group_id ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  // SIMs that are free, plus the one this device already has
  const used = new Set(devices.filter((d) => d.id !== device?.id && d.sim_id).map((d) => d.sim_id));
  const simChoices = sims.filter((s) => !used.has(s.id) && ["stock", "active", "suspended"].includes(s.status));
  const proto = PROTOCOL_INFO[f.protocol] ?? PROTOCOL_INFO.other;
  const orgGroups = groups.filter((g) => g.org_id === f.org_id);
  const simNo = sims.find((s) => s.id === f.sim_id)?.msisdn;

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (device) {
        await adminApi("PATCH", `devices/${device.id}`, clean({ org_id: f.org_id, name: f.name, model: f.model, sim_id: f.sim_id, status: f.status, notes: f.notes, icon: f.icon, group_id: f.group_id }));
        await onSaved(`Saved ${f.name}.`);
        onClose();
      } else {
        const r = await adminApi<{ adopted: boolean }>("POST", "devices", clean({ org_id: f.org_id, name: f.name, unique_id: f.unique_id, model: f.model, protocol: f.protocol, sim_id: f.sim_id, notes: f.notes, icon: f.icon, group_id: f.group_id }));
        await onSaved(`${r.adopted ? "Registered" : "Added"} ${f.name}. It will appear on the map as soon as it reports.`);
        setCreated(f.name);
      }
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  const setup = (
    <div className="wide asetup">
      <h3>Tracker setup — {proto.label}</h3>
      {simNo ? <p className="sub">Send these as SMS to the tracker's SIM <b className="mono">{simNo}</b> (default password usually 0000):</p>
        : <p className="sub">Settings for the tracker:</p>}
      <pre>{proto.setup().join("\n")}</pre>
      <p className="sub">Before moving an existing unit over from another platform, send <span className="mono">RCONF</span> first and save the reply so it can be rolled back.</p>
    </div>
  );

  if (created) {
    return (
      <Drawer title={`${created} added`} onClose={onClose} footer={<button className="btn-primary" onClick={onClose}>Done</button>}>
        <div className="aform">
          <Notice>Created on the tracking server, linked for history, and assigned to {orgs.find((o) => o.id === f.org_id)?.name}.</Notice>
          {setup}
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer title={device ? device.name : prefill.unique_id ? "Register device" : "Add device"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="dev-form" disabled={busy}>{busy ? "Saving…" : device ? "Save changes" : "Add device"}</button></>}>
      <form id="dev-form" className="aform" onSubmit={save}>
        <Field label="Company" wide>
          <select value={f.org_id} onChange={(e) => setF({ ...f, org_id: e.target.value, group_id: "" })} required>
            <option value="" disabled>Choose a company…</option>
            {orgs.filter((o) => o.status !== "closed").map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
        <Field label="Name on the map" hint="e.g. registration or vessel name"><input value={f.name} onChange={set("name")} required maxLength={80} /></Field>
        <Field label="Device ID / IMEI" hint={device ? "Can't be changed — register a new device instead." : "The ID the tracker sends (often printed on the unit)."}>
          <input className="mono" value={f.unique_id} onChange={set("unique_id")} required disabled={!!device} pattern="[A-Za-z0-9_\-]{4,40}" />
        </Field>
        <Field label="Model"><input value={f.model} onChange={set("model")} placeholder="e.g. ST-901, FMC130" maxLength={60} /></Field>
        <Field label="Protocol">
          <select value={f.protocol} onChange={set("protocol")} disabled={!!device}>
            {Object.entries(PROTOCOL_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}{v.port ? ` (port ${v.port})` : ""}</option>)}
          </select>
        </Field>
        <Field label="Icon on map" wide>
          <div className="icon-grid" role="group">
            {Object.entries(ICONS).map(([k, v]) => (
              <button type="button" key={k} title={v.hint ? `${v.label} — ${v.hint}` : v.label} className={f.icon === k ? "on" : ""}
                onClick={() => setF({ ...f, icon: k })}>
                <span dangerouslySetInnerHTML={{ __html: iconSvg(k, 18) }} /><small>{v.label}</small>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Group" hint={orgGroups.length ? undefined : "Add groups from Companies → this company."}>
          <select value={f.group_id} onChange={set("group_id")} disabled={!orgGroups.length}>
            <option value="">No group</option>
            {orgGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="SIM card" hint={simChoices.length ? undefined : "No free SIMs — add one under SIM cards first."}>
          <select value={f.sim_id} onChange={set("sim_id")}>
            <option value="">No SIM recorded</option>
            {simChoices.map((s) => <option key={s.id} value={s.id}>{s.msisdn} · {s.carrier ?? ""}{s.status === "stock" ? "" : ` · ${s.status}`}</option>)}
          </select>
        </Field>
        {device && (
          <Field label="Status" hint={f.status !== "active" ? "Suspended/retired devices are disabled on the tracking server and stop updating. A retired device can be removed (bottom of this panel)." : "To remove a device for good, set it to Retired and save first."}>
            <select value={f.status} onChange={set("status")}>
              <option value="active">Active</option><option value="suspended">Suspended</option><option value="retired">Retired</option>
            </select>
          </Field>
        )}
        <Field label="Internal notes" wide><textarea value={f.notes} onChange={set("notes")} rows={2} maxLength={2000} placeholder="Install location, wiring (ACC connected?), vehicle details…" /></Field>
        {device && (
          <div className="wide kv">
            <span>Tracking server ID</span><span className="mono">{device.traccar_id ?? "—"}</span>
            <span>Last report</span><span>{fmtDateTime(device.device_state?.seen_at)}</span>
            <span>Added</span><span>{fmtDateTime(device.created_at)}</span>
          </div>
        )}
        {err && <div className="error wide">{err}</div>}
        {setup}
      </form>
      {device && <div className="aform"><DeviceCommands device={device} role={role} /></div>}
      {device && role === "skyward_admin" && <RemoveDevice device={device} onRemoved={async () => { await onSaved(`Removed ${device.name}.`); onClose(); }} />}
    </Drawer>
  );
}

/** V3.9.5 — remove a device for good (admins). Only retired devices; type the name to confirm. */
function RemoveDevice({ device, onRemoved }: { device: AdminDevice; onRemoved: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const retired = device.status === "retired";
  async function remove() {
    setBusy(true); setErr(null);
    try { await adminApi("POST", `devices/${device.id}/remove`, { confirm: typed.trim() }); await onRemoved(); }
    catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }
  return (
    <section className="aform dev-remove" aria-label="Remove device">
      <h3 className="wide">Remove device</h3>
      {!retired ? (
        <p className="wide sub">A device must be <b>Retired</b> before it can be removed. Set Status to Retired above, save, then open it again.</p>
      ) : !open ? (
        <div className="wide"><p className="sub">Removes {device.name} from Tracking Fiji and the tracking server for good, with its history, events and logs. Its SIM goes back to stock and any fleet record is unlinked.</p>
          <button type="button" className="btn-ghost danger" onClick={() => setOpen(true)}>Remove device…</button></div>
      ) : (
        <div className="wide">
          <p className="sub"><b>This can't be undone.</b> Type <span className="mono">{device.name}</span> to confirm.</p>
          <div className="row-inline">
            <input value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Type the device name to confirm" placeholder={device.name} />
            <button type="button" className="btn-primary danger-fill" disabled={busy || typed.trim() !== device.name} onClick={remove}>{busy ? "Removing…" : "Remove for good"}</button>
            <button type="button" className="btn-ghost" onClick={() => { setOpen(false); setTyped(""); }}>Cancel</button>
          </div>
        </div>
      )}
      {err && <div className="error wide">{err}</div>}
    </section>
  );
}
